import React, { useRef } from 'react';
import { ITranslation, SearchParams, YadoHotel, YadoRoom, TicketSplits, Package } from '../types';
import styles from '../styles/HotelPlansSearch.module.scss';
import { getImage } from '../../utils/pathHelper';
import clockIcon from '../images/clock.svg';
import { formatQuery } from '../utils';
import { addDays, format, subDays } from 'date-fns';
import i18n from '../../i18n';
import YadoPlanCancellationPolicy from '../components/YadoPlanCancellationPolicy';
import parse, { HTMLReactParserOptions, domToReact } from 'html-react-parser';
import { formatDateWithDayOfWeek } from '../../utils/dateHelper';

type PackageModalInnerProps = {
  hotel: YadoHotel;
  packageItem: Package;
  room: YadoRoom;
  priceDisplay: string;
  localizedItemName: ITranslation;
  localizedAttributes: {
    mainAmenities: Record<string, string | string[]>;
    otherAmenities: Record<string, string>;
  };
  icons: {
    foodIcon: string;
    noFoodIcon: string;
    hotelIcon: string;
    smokingIcon: string;
    noSmokingIcon: string;
    wifiIcon: string;
    yadoIcon: string;
    bedIcon: string;
    onsenIcon: string;
    ticketIcon: string;
    ticketIconSolid: string;
    skiIcon: string;
    commonTicketIcon: string;
    plusIcon: string;
  };
  query: SearchParams;
};

const PackageModalInner: React.FC<PackageModalInnerProps> = ({
  packageItem,
  hotel,
  room,
  localizedItemName,
  localizedAttributes,
  icons,
  query,
  priceDisplay
}) => {
  const plan = room.yado_plan;
  const gon = (window as any).gon;
  const locale = gon.locale;
  const reservationDeadline = subDays(query.checkinDate, room.yado_plan.closing_days_before_checkin);
  const planRef = useRef(null);

  let adults, children;

  if (!packageItem.ticket_splits) {
    // リフト券付きプランで子供の提供枚数ルールが決まっていないための一時的処理
    const childAgeRangeStart = 6;
    const childAgeRangeEnd = 12;

    const childrenInAgeRange = query.people.children
      .filter(child => child.age >= childAgeRangeStart && child.age <= childAgeRangeEnd)
      .length;

    adults = query.people.adult;
    children = childrenInAgeRange;
  } else {
    adults = packageItem.ticket_splits.adults;
    children = packageItem.ticket_splits.children;
  }

  const handleReserve = () => {
    const formattedQuery = {
      ...formatQuery(query),
      packageItemId: packageItem.id.toString(),
    };
    const searchParams = new URLSearchParams(formattedQuery).toString();

    const url = `/snow/stay-packages/payment/new?${searchParams}`;

    window.location.href = url;
  };

  const mealTag = () => {
    if (plan.breakfast && plan.dinner) return <span><img src={icons.foodIcon} alt="food" className="mr-2" />{i18n.t('accommodations.breakfast_and_dinner_included')}</span>;
    if (plan.breakfast) return <span><img src={icons.foodIcon} alt="food" className="mr-2" />{i18n.t('accommodations.breakfast_included')}</span>;
    if (plan.dinner) return <span><img src={icons.foodIcon} alt="food" className="mr-2" />{i18n.t('accommodations.dinner_included')}</span>;
    return <span><img src={icons.noFoodIcon} alt="no food" className="mr-2" />{i18n.t('accommodations.overnight_stay_without_meals')}</span>;
  };

  const getAmenityIcon = (key: string) => {
    switch (key) {
      case "smoking":
        return room.room_information[key] ? icons.smokingIcon : icons.noSmokingIcon;
      case "bed_type":
        return icons.bedIcon;
      case "free_wifi":
        return icons.wifiIcon;
      case "size":
        return icons.yadoIcon;
      case "private_open_air_bath":
        return icons.onsenIcon;
      default:
        return "";
    }
  };

  const ticketIncludedScrollOptions = {
    replace: (domNode: any) => {
      if (domNode?.name === 'span') {
        const childrenContent = domNode.children.map((child: any) => child.data);

        return (
          <span
            className={styles.scrollToDescription}
            onClick={(e) => {
              planRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {childrenContent}
          </span>
        );
      }
    },
  };

  return (
    <>
      <div className={styles.closingDateNote}>
        <div className={styles.title}>
          <img src={getImage(clockIcon)} alt="more images" className="mr-1" />
          {i18n.t('stay_packages.hotels.show.make_your_reservation_early')}
        </div>
        <div className={styles.description}>
          {i18n.t('stay_packages.hotels.show.reservations_for_this_checkin_date_can_be_made_up_until_while_supplies_last', {date: formatDateWithDayOfWeek(reservationDeadline)})}
        </div>
      </div>
      <div className={`${styles.infoCard} mb-3`}>
        <div className="d-flex align-items-center mb-2">
          <img src={icons.hotelIcon} className="mr-2" />
          <p className={`mb-0 ${styles.infoTitle}`}>{hotel.name}</p>
        </div>
        <div className="row no-gutters">
          <div className="col-lg-4 col-12">
            <div className={styles.infoImageWrapper}>
              <img src={room.images[0]} alt={room.localized_name[locale]} />
            </div>
          </div>
          <div className="col-lg-8 col-12">
            <div className={styles.infoContent}>
              <div className={styles.infoTitle}>{room.localized_name[locale]}</div>
              <div className={styles.infoSubTitle}>
                <span>{mealTag()}</span>
                <span>{i18n.t('accommodations.checkin')}: {hotel.standardCheckinTime}</span>
                <span>{i18n.t('accommodations.checkout')}: {hotel.standardCheckoutTime}</span>
              </div>
              <div className={styles.facilityHeader}>{i18n.t('accommodations.main_facility')}</div>
              <ul className={styles.infoList}>
                {Object.entries(localizedAttributes.mainAmenities).map(([key, translatedValue]) => {
                  const icon = getAmenityIcon(key);
                  return (key === "bed_type") && Array.isArray(translatedValue) ?
                    <li key={key} className='d-flex'>
                      {icon && <img className="mr-2" src={icon} alt={key} />}
                      <div className='d-flex flex-column'>
                        {translatedValue.map((value, i) => {
                          return <span>{value} × {room.room_information['number_of_beds'][i]}</span>
                        })}
                      </div>
                    </li>
                  :
                    <li key={key}>
                      {icon && <img className="mr-2" src={icon} alt={key} />}
                      {translatedValue}
                    </li>
                  }
                )}
              </ul>
              <div className={styles.facilityHeader}>{i18n.t('accommodations.amenities')}</div>
              <ul className={styles.infoList}>
                {Object.entries(localizedAttributes.otherAmenities).map(([key, translatedValue]) => (
                  <li key={key}>{translatedValue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles.infoCard} mb-3`}>
        <div className="d-flex align-items-center mb-2">
          <img src={icons.ticketIconSolid} className="mr-2" />
          <p className={`mb-0 ${styles.infoTitle}`}>{i18n.t('accommodations.ski_products')}</p>
        </div>
        <div className="row no-gutters">
          <div className="col-lg-4 col-12">
            <div className={styles.infoImageWrapper}>
              <img src={packageItem.common_resorts[0]?.thumb_image_url} alt={localizedItemName[locale]} />
            </div>
          </div>
          <div className="col-lg-8 col-12">
            <div className={styles.infoContent}>
              <div className={styles.infoTitle}>{localizedItemName[locale]}</div>
              <div>
                {packageItem.common_resorts.map((resort) => (
                  <div className="d-flex align-items-center mb-2" key={resort.resort_code}>
                    <img src={icons.skiIcon} alt="ski icon" className="mr-2" />
                    <span>{resort.localized_name[locale]}</span>
                  </div>
                ))}
              </div>
              <div className={`${styles.infoList} d-block`}>
                {packageItem.snow_item ? (
                  <>
                    <div className="d-flex align-items-center">
                      <img src={icons.ticketIcon} className="mr-2" />
                      <div>
                        <span>{i18n.t('base.adult')} {i18n.t('accommodations.lift_ticket')}</span>
                        <span className="mx-1" aria-hidden="true">&times;</span>
                        <span>{adults}</span>
                      </div>
                    </div>

                    {children > 0 && (
                      <div className="d-flex align-items-center mt-2">
                        <img src={icons.ticketIcon} className="mr-2" />
                        <div>
                          <span>{i18n.t('base.children')} {i18n.t('accommodations.lift_ticket')}</span>
                          <span className="mx-1" aria-hidden="true">&times;</span>
                          <span>{children}</span>
                        </div>
                      </div>
                    )}

                    <div className="mt-2 text-muted">
                      {packageItem.ticket_splits?.child_as_adult ? (
                        <span className="d-block">{i18n.t('stay_packages.shared.child_as_adult_note_1')}</span>
                      ) : packageItem.ticket_splits?.child_ticket_free ? (
                        <span className="d-block">{i18n.t('stay_packages.shared.child_as_adult_note_2')}</span>
                      ) : (
                        <span>{i18n.t('accommodations.provided_per_person')}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="d-flex align-items-center">
                    <img src={icons.ticketIcon} className="mr-2" />
                    <p className={styles.ticketIncludedNote}>
                      {parse(i18n.t('stay_packages.shared.ticket_included_note'), ticketIncludedScrollOptions)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card mb-4 bg-white rounded-xl">
        <div className="card-header bg-white d-flex align-items-center mt-2">
          <h3 className="section-title">
            {i18n.t('accommodations.cancel_policy')}
          </h3>
        </div>
        <div className="card-body">
          {plan.cancellable ?
            <YadoPlanCancellationPolicy plan={plan} checkinDate={query.checkinDate} />
           :
            <p className="my-2" style={{ whiteSpace: 'pre-line' }}>
              {i18n.t('stay_packages.shared.this_plan_cannot_be_changed_or_canceled')}
            </p>
          }
        </div>
      </div>
      <div ref={planRef} className="card mt-4 pb-4 bg-white rounded-xl" style={{ marginBottom: '80px', scrollMarginTop: '80px' }}>
        <div className="card-header bg-white d-flex align-items-center mt-2">
          <h3 className="section-title">
            {i18n.t('accommodations.plan_description')}
          </h3>
        </div>
        <div className="card-body">
          <p style={{ whiteSpace: 'pre-line' }}>{plan.localized_description[locale]}</p>
        </div>
      </div>
      <div className={`d-flex justify-content-end align-items-center p-3 light-shadow-box ${styles.modalFooter}`}>
        <div className='text-sm text-muted mr-2'>
          { packageItem.total_price_ja.amount }
        </div>
        <div className={`text-primary font-weight-bold mr-3 mb-0 ${styles.bundleItemPrice}`}>
          {priceDisplay}
        </div>
        <button className="btn btn-primary rounded-pill font-weight-bold w-50" onClick={handleReserve}>
          {i18n.t('accommodations.proceed_to_booking')}
        </button>
      </div>
    </>
  );
};

export default PackageModalInner;
